/**
 * Observer hook for a live in-progress meeting.
 * Connects to SSE with ?meetingId=... so the backend replays historical segments
 * and progressive summaries on connect, then streams new events in real-time.
 *
 * Unlike useStreamingTranscript (recorder-side, uses WebSocket),
 * this hook is purely SSE-based and designed for read-only observer screens.
 */

import type {
  IProgressiveSummary,
  IProgressiveSummarySnapshot,
  ISpeakerHypothesisSnapshot,
  IStreamingSegment,
} from "@lexicanon/shared"
import { SSE_EVENTS } from "@lexicanon/shared"
import { useCallback, useEffect, useRef, useState } from "react"
import { createReconnectingEventSource } from "./useSSE"

export type { IProgressiveSummarySnapshot, ISpeakerHypothesisSnapshot }

interface ILiveMeetingState {
  segments: IStreamingSegment[]
  progressiveSummaries: IProgressiveSummarySnapshot[]
  latestSummary: IProgressiveSummary | null
  isStreaming: boolean
  interimText: string
  interimSpeaker: string
  /** Per-label speaker-hypothesis snapshots from the backend state machine,
   *  keyed by diarization label. State-only plumbing for the deferred observer
   *  confirm-pill UI (plan wave B4); no rendering yet. */
  speakerHypotheses: Map<string, ISpeakerHypothesisSnapshot>
  /** True while the SSE connection is down and a reconnect is pending/in-flight
   *  (5xx / pod restart). Additive — existing consumers can ignore it. */
  isReconnecting: boolean
}

interface ISSEMessage extends Partial<ISpeakerHypothesisSnapshot> {
  type: string
  meetingId?: string
  segment?: IStreamingSegment
  text?: string
  speaker?: string
  topics?: string[]
  currentDiscussion?: string
  keyPoints?: string[]
  speakerCount?: number
  _version?: number
  _timestamp?: string
  // speaker_identified (legacy) — `label` comes from the hypothesis snapshot above
  name?: string
  confirmed?: boolean
}

/**
 * Connect to SSE for a specific in-progress meeting.
 * On connect, backend replays all historical transcript segments and progressive
 * summary snapshots, then continues streaming new events in real-time.
 */
export function useLiveMeeting(meetingId: string, companyId: string | null | undefined): ILiveMeetingState {
  const [segments, setSegments] = useState<IStreamingSegment[]>([])
  const [progressiveSummaries, setProgressiveSummaries] = useState<IProgressiveSummarySnapshot[]>([])
  const [latestSummary, setLatestSummary] = useState<IProgressiveSummary | null>(null)
  const [isStreaming, setIsStreaming] = useState(false)
  const [interimText, setInterimText] = useState("")
  const [interimSpeaker, setInterimSpeaker] = useState("")
  const [speakerHypotheses, setSpeakerHypotheses] = useState<Map<string, ISpeakerHypothesisSnapshot>>(new Map())
  const [isReconnecting, setIsReconnecting] = useState(false)
  const speakerNamesRef = useRef<Map<string, string>>(new Map())
  const meetingIdRef = useRef(meetingId)
  meetingIdRef.current = meetingId

  const handleMessage = useCallback((data: ISSEMessage): void => {
    if (data.meetingId && data.meetingId !== meetingIdRef.current) return

    switch (data.type) {
      case SSE_EVENTS.STREAMING_SEGMENT:
        if (data.segment) {
          const seg = {
            ...data.segment,
            speaker: speakerNamesRef.current.get(data.segment.speaker) ?? data.segment.speaker,
          }
          setSegments((prev) => [...prev, seg])
          setInterimText("")
          setInterimSpeaker("")
          setIsStreaming(true)
        }
        break

      case SSE_EVENTS.STREAMING_INTERIM:
        setInterimText(data.text ?? "")
        setInterimSpeaker(speakerNamesRef.current.get(data.speaker ?? "") ?? data.speaker ?? "")
        setIsStreaming(true)
        break

      case SSE_EVENTS.SPEAKER_IDENTIFIED:
        if (data.label && data.name) {
          // Append "?" suffix for tentative matches; confirmed matches use the plain name
          const displayName = data.confirmed === false ? `${data.name}?` : data.name
          speakerNamesRef.current.set(data.label, displayName)
          // Retroactively rename already-stored segments with this label (or a prior tentative label)
          setSegments((prev) =>
            prev.map((s) =>
              s.speaker === data.label || s.speaker === `${data.name}?` ? { ...s, speaker: displayName } : s,
            ),
          )
        }
        break

      case SSE_EVENTS.SPEAKER_HYPOTHESIS:
        if (data.label) {
          const snap: ISpeakerHypothesisSnapshot = {
            label: data.label,
            status: data.status ?? "unidentified",
            confirmedName: data.confirmedName ?? null,
            best: data.best ?? null,
            candidates: data.candidates ?? [],
          }
          setSpeakerHypotheses((prev) => {
            const next = new Map(prev)
            next.set(snap.label, snap)
            return next
          })
          // When a label is confirmed, retroactively rename its segments so the
          // live transcript shows the real name (mirrors useStreamingTranscript).
          if (snap.status === "confirmed" && snap.confirmedName) {
            const confirmed = snap.confirmedName
            speakerNamesRef.current.set(snap.label, confirmed)
            setSegments((prev) =>
              prev.map((s) =>
                s.speaker === snap.label || s.speaker === `${confirmed}?` ? { ...s, speaker: confirmed } : s,
              ),
            )
          }
        }
        break

      case SSE_EVENTS.PROGRESSIVE_SUMMARY: {
        const summary: IProgressiveSummary = {
          topics: data.topics ?? [],
          currentDiscussion: data.currentDiscussion ?? "",
          keyPoints: data.keyPoints ?? [],
          speakerCount: data.speakerCount ?? 0,
        }
        setLatestSummary(summary)
        if (data._version !== undefined && data._timestamp !== undefined) {
          // Replayed or persisted snapshot — append to version history
          setProgressiveSummaries((prev) => {
            const exists = prev.some((s) => s.version === data._version)
            if (exists) return prev
            return [
              ...prev,
              { version: data._version as number, timestamp: data._timestamp as string, content: summary },
            ]
          })
        }
        break
      }

      case SSE_EVENTS.STREAMING_STOPPED:
      case SSE_EVENTS.STREAMING_FALLBACK:
        setIsStreaming(false)
        break
    }
  }, [])

  useEffect(() => {
    if (!companyId) return

    // Reset state when meeting changes
    setSegments([])
    setProgressiveSummaries([])
    setLatestSummary(null)
    setIsStreaming(false)
    setInterimText("")
    setInterimSpeaker("")
    setSpeakerHypotheses(new Map())
    setIsReconnecting(false)

    const url = `/api/companies/${companyId}/events?meetingId=${encodeURIComponent(meetingId)}`

    const onMessage = (event: MessageEvent<string>): void => {
      try {
        const data: ISSEMessage = JSON.parse(event.data)
        handleMessage(data)
      } catch {
        // Not a typed event (e.g. meeting metadata object) — ignore
      }
    }

    return createReconnectingEventSource(url, true, onMessage, (connected) => {
      setIsReconnecting(!connected)
      if (!connected) setIsStreaming(false)
    })
  }, [meetingId, companyId, handleMessage])

  return {
    segments,
    progressiveSummaries,
    latestSummary,
    isStreaming,
    interimText,
    interimSpeaker,
    speakerHypotheses,
    isReconnecting,
  }
}
